import * as React from 'react'
import { Dialog as SheetPrimitive } from '@base-ui/react/dialog'
import { XIcon } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import {
  type RadixDismissEvent,
  type OverlayDismissHandlerProps,
} from '@/components/ui/overlay-dismiss-handlers'

export type { RadixDismissEvent, OverlayDismissHandlerProps }

function Sheet({ onOpenChange, ...props }: SheetPrimitive.Root.Props) {
  const dismissHandlersRef = React.useRef<OverlayDismissHandlerProps>({})

  const handleOpenChange = React.useCallback(
    (open: boolean, details: SheetPrimitive.Root.ChangeEventDetails) => {
      if (!open) {
        const event: RadixDismissEvent = {
          preventDefault: () => {
            details.cancel()
          },
          target: details.event?.target ?? null,
        }

        if (details.reason === 'outside-press') {
          dismissHandlersRef.current.onPointerDownOutside?.(event)
          dismissHandlersRef.current.onInteractOutside?.(event)
        } else if (details.reason === 'focus-out') {
          dismissHandlersRef.current.onFocusOutside?.(event)
          dismissHandlersRef.current.onInteractOutside?.(event)
        } else if (details.reason === 'escape-key') {
          dismissHandlersRef.current.onEscapeKeyDown?.(event)
        }

        if (details.isCanceled) {
          return
        }
      }

      onOpenChange?.(open, details)
    },
    [onOpenChange]
  )

  return (
    <SheetDismissHandlersContext.Provider value={dismissHandlersRef}>
      <SheetPrimitive.Root
        data-slot='sheet'
        onOpenChange={handleOpenChange}
        {...props}
      />
    </SheetDismissHandlersContext.Provider>
  )
}

const SheetDismissHandlersContext = React.createContext<
  React.MutableRefObject<OverlayDismissHandlerProps>
>({ current: {} })

function SheetTrigger({
  asChild = false,
  render,
  children,
  nativeButton,
  ...props
}: SheetPrimitive.Trigger.Props & {
  asChild?: boolean
}) {
  let resolvedRender = render
  let resolvedChildren = children

  if (asChild) {
    const child = React.Children.only(children) as React.ReactElement<{
      children?: React.ReactNode
    }>
    resolvedRender = child
    resolvedChildren = child.props.children
  }

  let resolvedNativeButton = nativeButton
  if (resolvedNativeButton === undefined) {
    resolvedNativeButton =
      React.isValidElement(resolvedRender) && resolvedRender.type === 'button'
  }

  return (
    <SheetPrimitive.Trigger
      data-slot='sheet-trigger'
      nativeButton={resolvedNativeButton}
      render={resolvedRender}
      {...props}
    >
      {resolvedChildren}
    </SheetPrimitive.Trigger>
  )
}

function SheetClose({ ...props }: SheetPrimitive.Close.Props) {
  return <SheetPrimitive.Close data-slot='sheet-close' {...props} />
}

function SheetPortal({ ...props }: SheetPrimitive.Portal.Props) {
  return <SheetPrimitive.Portal data-slot='sheet-portal' {...props} />
}

function SheetOverlay({ className, ...props }: SheetPrimitive.Backdrop.Props) {
  return (
    <SheetPrimitive.Backdrop
      data-slot='sheet-overlay'
      className={cn(
        'fixed inset-0 z-50 bg-black/50 opacity-100 transition-opacity duration-300 data-ending-style:opacity-0 data-starting-style:opacity-0',
        className
      )}
      {...props}
    />
  )
}

const sheetSideClasses = {
  right:
    'inset-y-0 end-0 h-full w-3/4 border-s sm:max-w-sm data-starting-style:translate-x-full data-ending-style:translate-x-full',
  left: 'inset-y-0 start-0 h-full w-3/4 border-e sm:max-w-sm data-starting-style:-translate-x-full data-ending-style:-translate-x-full',
  top: 'inset-x-0 top-0 h-auto border-b data-starting-style:-translate-y-full data-ending-style:-translate-y-full',
  bottom:
    'inset-x-0 bottom-0 h-auto border-t data-starting-style:translate-y-full data-ending-style:translate-y-full',
} as const

type SheetContentProps = SheetPrimitive.Popup.Props &
  OverlayDismissHandlerProps & {
    side?: keyof typeof sheetSideClasses
    showOverlay?: boolean
  }

function SheetContent({
  className,
  children,
  side = 'right',
  showOverlay = true,
  onPointerDownOutside,
  onInteractOutside,
  onFocusOutside,
  onEscapeKeyDown,
  ...props
}: SheetContentProps) {
  const dismissHandlersRef = React.useContext(SheetDismissHandlersContext)

  React.useLayoutEffect(() => {
    dismissHandlersRef.current = {
      onPointerDownOutside,
      onInteractOutside,
      onFocusOutside,
      onEscapeKeyDown,
    }
    return () => {
      dismissHandlersRef.current = {}
    }
  }, [
    dismissHandlersRef,
    onPointerDownOutside,
    onInteractOutside,
    onFocusOutside,
    onEscapeKeyDown,
  ])

  return (
    <SheetPortal>
      {showOverlay ? <SheetOverlay /> : null}
      <SheetPrimitive.Popup
        data-slot='sheet-content'
        className={cn(
          'fixed z-50 flex flex-col gap-4 bg-background shadow-lg transition-transform duration-300 ease-in-out outline-none',
          sheetSideClasses[side],
          className
        )}
        {...props}
      >
        {children}
        <SheetPrimitive.Close className='absolute inset-e-4 top-4 rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none data-open:bg-secondary'>
          <XIcon className='size-4' />
          <span className='sr-only'>Close</span>
        </SheetPrimitive.Close>
      </SheetPrimitive.Popup>
    </SheetPortal>
  )
}

function SheetHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='sheet-header'
      className={cn('flex flex-col gap-1.5 p-4', className)}
      {...props}
    />
  )
}

function SheetFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='sheet-footer'
      className={cn('mt-auto flex flex-col gap-2 p-4', className)}
      {...props}
    />
  )
}

function SheetTitle({ className, ...props }: SheetPrimitive.Title.Props) {
  return (
    <SheetPrimitive.Title
      data-slot='sheet-title'
      className={cn('font-semibold text-foreground', className)}
      {...props}
    />
  )
}

function SheetDescription({
  className,
  ...props
}: SheetPrimitive.Description.Props) {
  return (
    <SheetPrimitive.Description
      data-slot='sheet-description'
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  )
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
